// @catalog-federation — signing profiles and signature verification
// ============================================================================
// The TypeScript twin of libs/federation/FederationSignatures.cs. Two profiles
// may appear in a federation payload:
//
//   • Hybrid (`Hybrid-Ed25519-MLDsa65`) — the trust key, organization keys,
//     `cpr.block`, `cid.sync`. Public key 1984 bytes; signature the raw
//     combined `ed25519_sig || mldsa65_sig` blob of 3373 bytes.
//   • ES256 — the `cid.jwt` OIDC token key. Public key the P-256
//     SubjectPublicKeyInfo DER; signature the IEEE P1363 `r || s` of 64 bytes,
//     exactly JWS ES256 and exactly what Web Crypto's ECDSA verifies.
//
// This package carries no crypto dependency. The host supplies its hybrid
// verifier ({@link HybridVerify}; the CID client binds its own `verifyHybrid`),
// SHA-512 and ES256 come from Web Crypto, present in browsers and in Node.

import type { JsonObject } from './json';

/** `Catalog.HybridCrypto.SigningProfile`: the family name and the parameter set. */
export interface SigningProfile {
    readonly algorithm: string;
    readonly parameterSet: string;
}

/** `SigningProfiles.HybridEd25519MlDsa65`. */
export const HYBRID_PROFILE: SigningProfile = Object.freeze({
    algorithm: 'Hybrid-Ed25519-MLDsa',
    parameterSet: 'Hybrid-Ed25519-MLDsa65',
});

/** `FederationSigningProfiles.Es256` — the `cid.jwt` key. */
export const ES256_PROFILE: SigningProfile = Object.freeze({
    algorithm: 'ECDSA',
    parameterSet: 'ES256',
});

export const HYBRID_PUBLIC_KEY_LENGTH = 1984;
export const HYBRID_SIGNATURE_LENGTH = 3373;
export const ES256_SIGNATURE_LENGTH = 64;

/**
 * A P-256 SubjectPublicKeyInfo with an uncompressed point is these 27 bytes
 * (SEQUENCE, id-ecPublicKey, prime256v1, BIT STRING, 0x04) and 64 more: the
 * form `JwtSigningKeyService` exports. Whether the point is on the curve is
 * left to Web Crypto when the key is imported to verify.
 */
const P256_SPKI_PREFIX = Uint8Array.of(
    0x30, 0x59, 0x30, 0x13, 0x06, 0x07, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x02, 0x01,
    0x06, 0x08, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x03, 0x01, 0x07, 0x03, 0x42, 0x00, 0x04,
);
export const ES256_PUBLIC_KEY_LENGTH = P256_SPKI_PREFIX.length + 64; // 91

export function sameProfile(a: SigningProfile, b: SigningProfile): boolean {
    return a.algorithm === b.algorithm && a.parameterSet === b.parameterSet;
}

/** The profile as payloads carry it. */
export function profileJson(profile: SigningProfile): JsonObject {
    return { algorithm: profile.algorithm, parameter_set: profile.parameterSet };
}

/**
 * Null when `publicKey` is a well-formed key of `profile`, else the defect.
 * Profiles other than the two above are refused, as in C#.
 */
export function validatePublicKey(profile: SigningProfile, publicKey: Uint8Array): string | null {
    if (sameProfile(profile, HYBRID_PROFILE)) {
        return publicKey.length === HYBRID_PUBLIC_KEY_LENGTH
            ? null
            : `a ${profile.parameterSet} public key is ${HYBRID_PUBLIC_KEY_LENGTH} bytes; got ${publicKey.length}`;
    }
    if (sameProfile(profile, ES256_PROFILE)) {
        const prefixed = publicKey.length === ES256_PUBLIC_KEY_LENGTH
            && P256_SPKI_PREFIX.every((b, i) => publicKey[i] === b);
        return prefixed ? null : 'an ES256 public key must be a P-256 SubjectPublicKeyInfo with an uncompressed point';
    }
    return `unsupported signing profile ${profile.algorithm}/${profile.parameterSet}`;
}

/**
 * The host's hybrid verifier: true only when `signature` — the combined
 * 3373-byte `ed25519_sig || mldsa65_sig` blob — verifies over `message` under
 * the 1984-byte combined public key, both halves. Lengths are checked before it
 * is called; an exception it throws counts as a failed verification.
 */
export type HybridVerify = (message: Uint8Array, signature: Uint8Array, publicKey: Uint8Array) => boolean;

/** Verifies the signatures federation payloads carry. False for an
 *  unsupported profile, never a throw (C# `TryVerify`). */
export interface SignatureVerifier {
    verify(message: Uint8Array, signature: Uint8Array, publicKey: Uint8Array, profile: SigningProfile): Promise<boolean>;
}

/** A {@link SignatureVerifier} over the host's hybrid primitive and Web Crypto's ES256. */
export function federationSignatureVerifier(verifyHybrid: HybridVerify): SignatureVerifier {
    return {
        async verify(message, signature, publicKey, profile) {
            if (sameProfile(profile, HYBRID_PROFILE)) {
                if (publicKey.length !== HYBRID_PUBLIC_KEY_LENGTH || signature.length !== HYBRID_SIGNATURE_LENGTH) {
                    return false;
                }
                try {
                    return verifyHybrid(message, signature, publicKey) === true;
                } catch {
                    return false;
                }
            }
            if (sameProfile(profile, ES256_PROFILE)) return verifyEs256(message, signature, publicKey);
            return false;
        },
    };
}

async function verifyEs256(message: Uint8Array, signature: Uint8Array, publicKey: Uint8Array): Promise<boolean> {
    if (validatePublicKey(ES256_PROFILE, publicKey) !== null) return false;
    if (signature.length !== ES256_SIGNATURE_LENGTH) return false;
    try {
        const key = await crypto.subtle.importKey(
            'spki', publicKey as BufferSource, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
        return await crypto.subtle.verify(
            { name: 'ECDSA', hash: 'SHA-256' }, key, signature as BufferSource, message as BufferSource);
    } catch {
        return false;
    }
}
